import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Check, Lock } from "lucide-react";
import { COVER_IMG } from "@/lib/api";
import { CountdownTimer } from "./CountdownTimer";
import { CheckoutDialog } from "./CheckoutDialog";

const INCLUDED = [
  "4-Day Upper/Lower Hypertrophy Split",
  "8-Week Progressive Overload Cycle",
  "Detailed Coaching Cues for Every Exercise",
  "Active Recovery & Core Workouts",
  "Evidence-Based Supplement Guide",
  "Progressive Overload Tracking Log",
];

const MEAL_PLAN_INCLUDED = [
  "41-Page Meal Plan Lookbook (PDF)",
  "23 Anime-Named Recipes × 5 Portion Tiers",
  "Offline Interactive Meal Calculator",
  "Done-For-You Weeks + Shopping Lists",
  "Protein / Carb / Fat Swap System",
  "Built for Rewe, Lidl & Aldi",
];

const MAIN_PRODUCT = { id: "anime-physique-program", name: "Anime Physique Program", price: "19.99" };
const MEAL_PLAN = { id: "meal-plan", name: "Anime Physique Meal Plan", price: "24.99" };

const CONSULTATION = {
  id: "consultation",
  name: "1-on-1 Online Consultation",
  price: "99.00",
  fulfillment: "service",
};

// Remaining spots only — never the booked count, and never the capacity.
const spotsLabel = (n) =>
  n === 0 ? "SOLD OUT" : n === 1 ? "ONLY 1 SPOT LEFT" : `ONLY ${n} SPOTS LEFT`;

const COACHING_INCLUDED = [
  "4-Week 1-on-1 Coaching Block",
  "45-Min Kickoff Call",
  "45-Min Week-4 Review Call",
  "Private Coaching Channel on Discord",
  "Custom Training Plan For Your Gym",
  "Nutrition Targets Tuned To Your Goal",
  "Form Video Reviews Between Calls",
];

export const ProductSlider = ({ paypalEnabled, products = [] }) => {
  const sliderRef = useRef(null);
  const [checkoutProduct, setCheckoutProduct] = useState(null);

  // The card renders off the backend flag, so launching is a one-line server
  // change with no frontend deploy.
  const consultation = products.find((p) => p.id === "consultation");
  const coachingLive = Boolean(consultation?.active);
  const spotsLeft = consultation?.spots_left ?? 0;
  const soldOut = coachingLive && spotsLeft === 0;

  const scroll = (dir) => {
    sliderRef.current?.scrollBy({ left: dir * (sliderRef.current.offsetWidth * 0.8), behavior: "smooth" });
  };

  return (
    <section id="pricing" className="py-24 sm:py-32 relative">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 30%, rgba(217,4,41,0.1) 0%, transparent 55%)" }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
          <div>
            <p className="section-label mb-4">// THE ARSENAL //</p>
            <h2 className="font-anton text-4xl sm:text-5xl uppercase tracking-tight">
              Pick your <span className="red-text">weapon</span>
            </h2>
          </div>
          <div className="flex gap-3">
            <button data-testid="slider-prev-btn" onClick={() => scroll(-1)} className="border border-[#2A2A2A] p-3 hover:border-[#39FF14] hover:text-[#39FF14] transition-colors" aria-label="Previous">
              <ChevronLeft size={20} />
            </button>
            <button data-testid="slider-next-btn" onClick={() => scroll(1)} className="border border-[#2A2A2A] p-3 hover:border-[#39FF14] hover:text-[#39FF14] transition-colors" aria-label="Next">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div ref={sliderRef} data-testid="product-slider" className="snap-slider flex gap-8 overflow-x-auto pb-4">
          {/* Main product */}
          <div
            data-testid="product-card-main"
            className="snap-slide flex-shrink-0 w-[88%] sm:w-[560px] bg-[#111111] border-2 border-[#39FF14] relative"
            style={{ boxShadow: "10px 10px 0px #D90429" }}
          >
            <div className="absolute top-0 right-0 bg-[#D90429] font-bebas tracking-[0.2em] text-xs px-4 py-1.5 z-10">
              BEST SELLER
            </div>
            <div className="grid sm:grid-cols-2 h-full">
              <img src={COVER_IMG} alt="Anime Physique Program" className="w-full h-full object-contain object-top bg-[#050505] min-h-[200px] border-b sm:border-b-0 sm:border-r border-[#2A2A2A]" />
              <div className="p-7 flex flex-col">
                <h3 className="font-anton text-2xl uppercase red-text">Anime Physique Program</h3>
                <p className="font-jet text-[10px] text-[#52525B] tracking-widest mt-1 mb-5">DIGITAL PROGRAM • INSTANT DOWNLOAD</p>
                <ul className="space-y-2 mb-6">
                  {INCLUDED.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-xs text-[#A1A1AA]">
                      <Check size={14} className="text-[#39FF14] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex items-baseline gap-3 mb-6 mt-auto">
                  <span className="font-anton text-4xl text-[#F5F5F5]">€19.99</span>
                  <span className="font-bebas text-[#D90429] tracking-widest text-sm">ONE-TIME</span>
                </div>
                <button data-testid="buy-program-btn" onClick={() => setCheckoutProduct(MAIN_PRODUCT)} className="btn-brutal w-full">
                  Start Grinding
                </button>
              </div>
            </div>
          </div>

          {/* Meal Plan */}
          <div
            data-testid="product-card-meal-plan"
            className="snap-slide flex-shrink-0 w-[88%] sm:w-[560px] bg-[#111111] border-2 border-[#D90429] relative"
            style={{ boxShadow: "10px 10px 0px #39FF14" }}
          >
            <div className="absolute top-0 right-0 bg-[#39FF14] text-[#050505] font-bebas tracking-[0.2em] text-xs px-4 py-1.5 z-10">
              NEW DROP
            </div>
            <div className="grid sm:grid-cols-2 h-full">
              <img src="/mealplan-cover.jpg" alt="Anime Physique Meal Plan cover" className="w-full h-full object-contain object-top bg-[#050505] min-h-[200px] border-b sm:border-b-0 sm:border-r border-[#2A2A2A]" />
              <div className="p-7 flex flex-col">
                <h3 className="font-anton text-2xl uppercase neon-text">Anime Physique Meal Plan</h3>
                <p className="font-jet text-[10px] text-[#52525B] tracking-widest mt-1 mb-5">PDF + OFFLINE CALCULATOR • INSTANT DOWNLOAD</p>
                <ul className="space-y-2 mb-6">
                  {MEAL_PLAN_INCLUDED.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-xs text-[#A1A1AA]">
                      <Check size={14} className="text-[#39FF14] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex items-baseline gap-3 mb-6 mt-auto">
                  <span className="font-anton text-4xl text-[#F5F5F5]">€24.99</span>
                  <span className="font-bebas text-[#D90429] tracking-widest text-sm">ONE-TIME</span>
                </div>
                {/* empty:hidden — the timer renders null once its deadline passes,
                    and an empty wrapper would still push the price row out of
                    alignment with the other two cards. */}
                <div className="mb-4 empty:hidden">
                  <CountdownTimer label="LAUNCH WEEK ENDS IN" />
                </div>
                <button data-testid="buy-meal-plan-btn" onClick={() => setCheckoutProduct(MEAL_PLAN)} className="btn-brutal btn-brutal-green w-full">
                  Fuel The Grind
                </button>
              </div>
            </div>
          </div>

          {/* Coaching â€” same card format, locked/greyed until launch */}
          <div
            data-testid="product-card-consultation"
            className={`snap-slide flex-shrink-0 w-[88%] sm:w-[560px] border-2 relative ${
              coachingLive ? "bg-[#111111] border-[#F0C800]" : "bg-[#0A0A0A] border-[#2A2A2A]"
            }`}
            // Live card gets the same hard offset shadow as the other two, so all
            // three read as one system; yellow ties it to the cover art.
            style={coachingLive ? { boxShadow: "10px 10px 0px #D90429" } : undefined}
          >
            <div
              data-testid="coaching-ribbon"
              className={`absolute top-0 right-0 font-bebas tracking-[0.2em] text-xs px-4 py-1.5 z-10 ${
                !coachingLive || soldOut
                  ? "bg-[#2A2A2A] text-[#A1A1AA]"
                  : "bg-[#F0C800] text-[#050505]"
              }`}
            >
              {coachingLive ? spotsLabel(spotsLeft) : "COMING SOON"}
            </div>
            <div className="grid sm:grid-cols-2 h-full">
              <img
                src="/coaching-cover.jpg"
                alt="1-on-1 Coaching cover"
                className={`w-full h-full object-contain object-top bg-[#050505] min-h-[200px] border-b sm:border-b-0 sm:border-r border-[#2A2A2A] ${
                  coachingLive ? "" : "grayscale opacity-40"
                }`}
              />
              <div className={`p-7 flex flex-col ${coachingLive ? "" : "opacity-60"}`}>
                <h3
                  className={`font-anton text-2xl uppercase flex items-center gap-2 ${
                    coachingLive ? "yellow-text" : "text-[#A1A1AA]"
                  }`}
                >
                  {!coachingLive && <Lock size={20} className="text-[#52525B] flex-shrink-0" />}
                  1-on-1 Coaching
                </h3>
                <p className="font-jet text-[10px] text-[#52525B] tracking-widest mt-1 mb-5">
                  {coachingLive ? "4-WEEK BLOCK • 2 CALLS + ASYNC" : "PERSONAL COACHING • LIMITED SPOTS"}
                </p>
                <ul className="space-y-2 mb-6">
                  {COACHING_INCLUDED.map((item) => (
                    <li
                      key={item}
                      className={`flex items-start gap-2 text-xs ${
                        coachingLive ? "text-[#A1A1AA]" : "text-[#52525B]"
                      }`}
                    >
                      <Check
                        size={14}
                        className={`mt-0.5 flex-shrink-0 ${coachingLive ? "text-[#39FF14]" : "text-[#52525B]"}`}
                      />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex items-baseline gap-3 mb-6 mt-auto">
                  {coachingLive ? (
                    <>
                      <span className="font-anton text-4xl text-[#F5F5F5]">€99</span>
                      <span className="font-bebas text-[#D90429] tracking-widest text-sm">4-WEEK BLOCK</span>
                    </>
                  ) : (
                    <>
                      <span className="font-anton text-4xl text-[#52525B]">TBA</span>
                      <span className="font-bebas text-[#52525B] tracking-widest text-sm">IN DEVELOPMENT</span>
                    </>
                  )}
                </div>
                {coachingLive ? (
                  <button
                    data-testid="coaching-buy-btn"
                    disabled={soldOut}
                    onClick={() => setCheckoutProduct(CONSULTATION)}
                    className={`btn-brutal w-full ${soldOut ? "!bg-[#2A2A2A] !shadow-none cursor-not-allowed" : "btn-brutal-yellow"}`}
                  >
                    {soldOut ? "Sold Out" : "Claim Your Spot"}
                  </button>
                ) : (
                  <button disabled className="btn-brutal w-full !bg-[#2A2A2A] !shadow-none cursor-not-allowed">
                    In Development
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <CheckoutDialog
        open={checkoutProduct !== null}
        onOpenChange={(o) => !o && setCheckoutProduct(null)}
        paypalEnabled={paypalEnabled}
        product={checkoutProduct}
      />
    </section>
  );
};
